import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  Container,
  Grid,
  Header,
  Segment,
  Button,
  Icon,
  List,
  Card,
  Breadcrumb,
  Dimmer,
  Loader,
  Message,
} from 'semantic-ui-react'
import { fetchCustomerTypeBySlug } from '../../../lib/datocms'
import './Customers.css'

const CustomerDetail = () => {
  const { slug } = useParams()
  const [customer, setCustomer] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    loadCustomer()
  }, [slug])

  const loadCustomer = async () => {
    try {
      setLoading(true)
      const data = await fetchCustomerTypeBySlug(slug)
      if (!data) {
        setError('Customer type not found')
      } else {
        setCustomer(data)
      }
    } catch (err) {
      console.error('Error loading customer type:', err)
      setError('Failed to load customer information. Please try again later.')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <Dimmer active inverted>
        <Loader size='large'>Loading Customer Information...</Loader>
      </Dimmer>
    )
  }

  if (error) {
    return (
      <Container style={{ marginTop: '2em' }}>
        <Message negative>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
        <Button as={Link} to='/customers' primary>
          <Icon name='arrow left' /> Back to Customers
        </Button>
      </Container>
    )
  }

  if (!customer) {
    return null
  }

  return (
    <div className="customer-detail-page">
      {/* Breadcrumb */}
      <Container style={{ paddingTop: '20px' }}>
        <Breadcrumb>
          <Breadcrumb.Section as={Link} to='/'>Home</Breadcrumb.Section>
          <Breadcrumb.Divider />
          <Breadcrumb.Section as={Link} to='/customers'>Customers</Breadcrumb.Section>
          <Breadcrumb.Divider />
          <Breadcrumb.Section active>{customer.title}</Breadcrumb.Section>
        </Breadcrumb>
      </Container>

      {/* Hero Section */}
      <Segment className="customer-detail-hero" vertical>
        <Container>
          <Grid columns={2} stackable verticalAlign='middle'>
            <Grid.Column>
              <Header as='h1' inverted>
                {customer.title}
              </Header>
              <p className="customer-hero-description">
                {customer.shortDescription}
              </p>
              {customer.ctaText && (
                <Button 
                  primary 
                  size='large' 
                  as={customer.ctaLink ? 'a' : Link}
                  href={customer.ctaLink}
                  to={!customer.ctaLink ? '/signup' : undefined}
                >
                  {customer.ctaText}
                  <Icon name='arrow right' />
                </Button>
              )}
            </Grid.Column>
            <Grid.Column>
              {customer.image?.url && (
                <img 
                  src={customer.image.url} 
                  alt={customer.image.alt || customer.title}
                  style={{ width: '100%', borderRadius: '8px' }}
                />
              )}
            </Grid.Column>
          </Grid>
        </Container>
      </Segment>

      {/* Description Section */}
      <Segment vertical style={{ padding: '80px 0' }}>
        <Container>
          <Header as='h2' textAlign='center'>Overview</Header>
          <p style={{ fontSize: '1.1em', lineHeight: '1.8' }}>
            {customer.description}
          </p>
        </Container>
      </Segment>

      {/* Features Section */}
      {customer.features && customer.features.length > 0 && (
        <Segment vertical style={{ padding: '80px 0', backgroundColor: '#f8f9fa' }}>
          <Container>
            <Header as='h2' textAlign='center' style={{ marginBottom: '3em' }}>
              Key Features for {customer.title}
            </Header>
            <Grid columns={3} stackable>
              {customer.features.map(feature => (
                <Grid.Column key={feature.id}>
                  <div className="feature-box">
                    <Icon name='check circle' size='large' color='green' />
                    <Header as='h3'>{feature.title}</Header>
                    <p>{feature.description}</p>
                  </div>
                </Grid.Column>
              ))}
            </Grid>
          </Container>
        </Segment>
      )}

      {/* Benefits Section */}
      {customer.benefits && customer.benefits.length > 0 && (
        <Segment vertical style={{ padding: '80px 0' }}>
          <Container>
            <Grid columns={2} stackable>
              <Grid.Column>
                <Header as='h2'>Benefits</Header>
                <List bulleted size='large'>
                  {customer.benefits.map((benefit, index) => (
                    <List.Item key={index}>{benefit}</List.Item>
                  ))}
                </List>
              </Grid.Column>
              <Grid.Column>
                <Segment placeholder>
                  <Header icon>
                    <Icon name='chart line' />
                    Benefits Visualization
                  </Header>
                </Segment>
              </Grid.Column>
            </Grid>
          </Container>
        </Segment>
      )}

      {/* Use Cases Section */}
      {customer.useCases && customer.useCases.length > 0 && (
        <Segment vertical style={{ padding: '80px 0', backgroundColor: '#f8f9fa' }}>
          <Container>
            <Header as='h2' textAlign='center' style={{ marginBottom: '3em' }}>
              Common Use Cases
            </Header>
            <Grid columns={2} stackable>
              {customer.useCases.map((useCase, index) => (
                <Grid.Column key={index}>
                  <Segment>
                    <Icon name='lightbulb' color='yellow' size='large' />
                    <p style={{ marginTop: '10px' }}>{useCase}</p>
                  </Segment>
                </Grid.Column>
              ))}
            </Grid>
          </Container>
        </Segment>
      )}

      {/* Testimonials Section */}
      {customer.testimonials && customer.testimonials.length > 0 && (
        <Segment vertical style={{ padding: '80px 0' }}>
          <Container>
            <Header as='h2' textAlign='center' style={{ marginBottom: '3em' }}>
              What Our {customer.title} Say
            </Header>
            <Grid columns={2} stackable>
              {customer.testimonials.map(testimonial => (
                <Grid.Column key={testimonial.id}>
                  <Segment>
                    <Icon name='quote left' color='grey' />
                    <p style={{ fontStyle: 'italic', fontSize: '1.1em', margin: '20px 0' }}>
                      {testimonial.content}
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', marginTop: '20px' }}>
                      {testimonial.avatar?.url && (
                        <img 
                          src={testimonial.avatar.url} 
                          alt={testimonial.author}
                          style={{ width: '50px', height: '50px', borderRadius: '50%', marginRight: '15px' }}
                        />
                      )}
                      <div>
                        <strong>{testimonial.author}</strong>
                        <p style={{ margin: 0, color: '#666' }}>{testimonial.company}</p>
                      </div>
                    </div>
                  </Segment>
                </Grid.Column>
              ))}
            </Grid>
          </Container>
        </Segment>
      )}

      {/* Related Customer Types */}
      {customer.relatedCustomerTypes && customer.relatedCustomerTypes.length > 0 && (
        <Segment vertical style={{ padding: '80px 0', backgroundColor: '#f8f9fa' }}>
          <Container>
            <Header as='h2' textAlign='center' style={{ marginBottom: '3em' }}>
              Other Customer Types
            </Header>
            <Card.Group itemsPerRow={3} stackable>
              {customer.relatedCustomerTypes.map(related => (
                <Card key={related.id} as={Link} to={`/customers/${related.slug}`}>
                  <Card.Content>
                    {related.icon?.url && (
                      <div className="customer-icon">
                        <img src={related.icon.url} alt={related.icon.alt || related.title} />
                      </div>
                    )}
                    <Card.Header>{related.title}</Card.Header>
                    <Card.Description>{related.shortDescription}</Card.Description>
                  </Card.Content>
                  <Card.Content extra>
                    <Button primary fluid>
                      Learn More <Icon name='arrow right' />
                    </Button>
                  </Card.Content>
                </Card>
              ))}
            </Card.Group>
          </Container>
        </Segment>
      )}

      {/* CTA Section */}
      <Segment className="customer-detail-cta" inverted vertical>
        <Container textAlign='center'>
          <Header as='h2' inverted>
            Ready to Transform Your Business?
          </Header>
          <p style={{ fontSize: '1.1em', marginBottom: '2em' }}>
            Join other successful {customer.title.toLowerCase()} using Trading Systems.
          </p>
          <Button size='large' inverted as={Link} to='/signup'>
            Start Free Trial
          </Button>
          <Button size='large' inverted basic as={Link} to='/contact-us'>
            Schedule a Demo
          </Button>
        </Container>
      </Segment>
    </div>
  )
}

export default CustomerDetail